import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PropertySubmenuMemo } from '@/components/property/PropertySubmenu';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import { PropertyDetailHeader } from '@/pages/PropertyDetailHeader';
import { PropertyDetailContent } from '@/pages/PropertyDetailContent';
import { PropertyDetailActions } from '@/pages/PropertyDetailActions';

export const PropertyDetailPage = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();

  const property = useQuery(
    api.property.queries.getPropertyListing,
    propertyId ? { propertyId: propertyId as Id<'propertyListings'> } : 'skip'
  );

  const videos = useQuery(
    api.property.queries.getPropertyVideos,
    propertyId ? { propertyId: propertyId as Id<'propertyListings'> } : 'skip'
  );

  const latestVideo = videos?.[0];
  const hasCompletedVideo = latestVideo?.status === 'completed';
  const isLoading = property === undefined;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteListing = useMutation(api.property.mutations.deletePropertyListing);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyId) return;
    setIsDeleting(true);
    try {
      await deleteListing({ propertyId: propertyId as Id<'propertyListings'> });
      setIsDeleteDialogOpen(false);
      navigate(hasCompletedVideo ? '/property/ready' : '/property/draft');
    } catch (error) {
      console.error('Failed to delete property:', error);
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    navigate(hasCompletedVideo ? '/property/ready' : '/property/draft');
  };

  const handleGenerateVideo = () => {
    if (propertyId) {
      navigate(`/property/new?propertyId=${propertyId}`);
    }
  };

  const formatPrice = (price?: number, currency?: string) => {
    if (!price) return null;
    const currencySymbol =
      currency === 'EUR' ? '\u20ac' : currency === 'USD' ? '$' : currency || '\u20ac';
    return `${currencySymbol}${price.toLocaleString()}`;
  };

  return {
    submenu: <PropertySubmenuMemo />,
    mainContent: (
      <div className="h-full bg-gray-50 dark:bg-gray-950 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
          </div>
        )}

        {!isLoading && !property && (
          <div className="flex flex-col items-center justify-center h-full">
            <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Property not found
            </h2>
            <Button variant="outline" onClick={() => navigate('/property/draft')}>
              Back to Properties
            </Button>
          </div>
        )}

        {!isLoading && property && (
          <div className="p-6 max-w-4xl mx-auto">
            <PropertyDetailHeader
              property={property}
              latestVideo={latestVideo}
              hasCompletedVideo={hasCompletedVideo}
              onBack={handleBack}
              formatPrice={formatPrice}
            />

            <PropertyDetailContent
              property={property}
              latestVideo={latestVideo}
              hasCompletedVideo={hasCompletedVideo}
              lightboxOpen={lightboxOpen}
              lightboxIndex={lightboxIndex}
              onImageClick={handleImageClick}
              onLightboxClose={() => setLightboxOpen(false)}
            />

            <PropertyDetailActions
              property={property}
              hasCompletedVideo={hasCompletedVideo}
              isDeleteDialogOpen={isDeleteDialogOpen}
              isDeleting={isDeleting}
              onGenerateVideo={handleGenerateVideo}
              onDeleteClick={() => setIsDeleteDialogOpen(true)}
              onDeleteConfirm={handleDeleteConfirm}
              onDeleteCancel={() => {
                if (!isDeleting) setIsDeleteDialogOpen(false);
              }}
            />
          </div>
        )}
      </div>
    ),
  };
};
